import { useContext, useState } from "react";
import { Helmet } from "react-helmet";
import {
  IoInformationCircleOutline,
  IoPersonCircle,
  IoPersonCircleOutline,
  IoPodiumOutline,
  IoSettingsOutline,
} from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import {
  ContainerMenuButton,
  IconMenu,
  MenuButton,
  ModalContainer,
  ModalContent,
  Title,
} from "./styles";
import Leaderboard from "../../components/leaderboardmodal/Leaderboard";
import ProfilePage from "../../components/profilemodal/Profile";
import SettingsPage from "../../components/settingsmodal/Settings";
import { HowToPlay } from "../../components/howtoplaymodal/HowToPlay";
import { Container, Heading, Page } from "../../global/styles/styles";
import {
  logHowToPlayView,
  logMenuClick,
  ThemeContext,
  UserContext,
} from "../../global";

const currentYear = new Date().getFullYear();

const renderModal = ({ onClick, page }) => {
  return (
    <ModalContainer onClick={onClick}>
      <ModalContent onClick={(e) => e.stopPropagation()}>{page}</ModalContent>
    </ModalContainer>
  );
};

const HomePage = ({ isMobile }) => {
  const navigate = useNavigate();
  const { user, setUser } = useContext(UserContext);
  const { theme, myTheme, setTheme } = useContext(ThemeContext);
  const [multiButtonText, setMultiButtonText] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showLeaderboardModal, setShowLeaderboardModal] = useState(false);
  const [showHowToPlayModal, setShowHowToPlayModal] = useState(false);
  const onClickProfile = () => setShowProfileModal(true);
  const onClickSettings = () => setShowSettingsModal(true);
  const onClickLeaderboard = () => setShowLeaderboardModal(true);
  const onClickHowToPlay = () => {
    logHowToPlayView();
    setShowHowToPlayModal(true);
  };

  function handleClick(e) {
    logMenuClick({ itemName: e.target.value });
    navigate(e.target.value);
  }

  return (
    <>
      <Helmet>
        <title>CompuTiles</title>
        <meta
          name="description"
          content="CompuTiles is a fast-paced math puzzle game. Solve number challenges, improve mental math, and play solo or with friends."
        />
        <link rel="canonical" href="https://www.computiles.com/" />

        {/* Open Graph */}
        <meta property="og:title" content="CompuTiles" />
        <meta
          property="og:description"
          content="Play solo or challenge friends in this addicting math puzzle game. Train your brain and improve problem-solving skills."
        />
        <meta
          property="og:image"
          content="https://www.computiles.com/numero_icon-transformed.png"
        />
        <meta property="og:url" content="https://www.computiles.com/" />
        <meta property="og:type" content="website" />

        {/* Twitter */}
        <meta name="twitter:title" content="CompuTiles" />
        <meta
          name="twitter:description"
          content="A fast, fun mental-math puzzle game you can play solo or with friends."
        />
        <meta
          name="twitter:image"
          content="https://www.computiles.com/numero_icon-transformed.png"
        />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <Page>
        {showHowToPlayModal && (
          <HowToPlay
            theme={theme}
            setTheme={setTheme}
            onClick={() => setShowHowToPlayModal(false)}
          />
        )}
        {showProfileModal &&
          renderModal({
            onClick: () => setShowProfileModal(false),
            page: <ProfilePage user={user} setUser={setUser} />,
          })}
        {showSettingsModal &&
          renderModal({
            onClick: () => setShowSettingsModal(false),
            page: (
              <SettingsPage
                theme={theme}
                myTheme={myTheme}
                setTheme={setTheme}
              />
            ),
          })}
        {showLeaderboardModal &&
          renderModal({
            onClick: () => setShowLeaderboardModal(false),
            page: <Leaderboard user={user} />,
          })}
        <IconMenu>
          <div
            onClick={onClickProfile}
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: "7px",
            }}
          >
            {user.username ? (
              <>
                <Heading>Hello {user.username}</Heading>
                <IoPersonCircle
                  size={"2em"}
                  color={
                    theme.dark
                      ? myTheme.colors.lightText
                      : myTheme.colors.darkText
                  }
                />
              </>
            ) : (
              <IoPersonCircleOutline
                size={"2em"}
                color={
                  theme.dark
                    ? myTheme.colors.lightText
                    : myTheme.colors.darkText
                }
              />
            )}
          </div>
          <div onClick={onClickSettings}>
            <IoSettingsOutline
              size={"2em"}
              color={
                theme.dark ? myTheme.colors.lightText : myTheme.colors.darkText
              }
            />
          </div>
          <div onClick={onClickLeaderboard}>
            <IoPodiumOutline
              size={"2em"}
              color={
                theme.dark ? myTheme.colors.lightText : myTheme.colors.darkText
              }
            />
          </div>
        </IconMenu>
        <Container $flex={2} />
        <Container $flex={3} $flexDirection={"column"}>
          <Container $flexDirection={"row"}>
            <Title>CompuTiles</Title>
          </Container>
          <Heading $size={"16px"}>Practice your math skills!</Heading>
        </Container>
        <ContainerMenuButton $flex={6} $flexDirection={"column"}>
          <MenuButton value={"/singleplayer"} onClick={handleClick}>
            Single Player
          </MenuButton>
          {isMobile ? (
            <MenuButton value={"/multiplayer"} onClick={handleClick}>
              Multiplayer
            </MenuButton>
          ) : (
            <MenuButton
              onMouseEnter={() => setMultiButtonText(true)}
              onMouseLeave={() => setMultiButtonText(false)}
              value={"/multiplayer"}
              onClick={handleClick}
            >
              {!multiButtonText ? (
                <>Multiplayer</>
              ) : (
                <>(table or phone recommended)</>
              )}
            </MenuButton>
          )}
          <MenuButton onClick={onClickHowToPlay}>
            <Container $gap={"5px"}>
              How to Play
              <IoInformationCircleOutline />
            </Container>
          </MenuButton>
        </ContainerMenuButton>
        <Container
          style={{ alignItems: "flex-end", paddingBottom: "7px" }}
          $flex={3}
        >
          <Heading $size={"12px"} style={{ maxWidth: "85%" }}>
            © {currentYear} CompuTiles. All rights reserved. Contact us at
            admin@computiles.com
          </Heading>
          <Heading
            $size={"12px"}
            style={{ position: "absolute", right: "10px" }}
          >
            v1.3.0
          </Heading>
          <div style={{ display: "none" }}>
            <a href="/singleplayer"></a>
            <a href="/multiplayer"></a>
          </div>
        </Container>
      </Page>
    </>
  );
};

export default HomePage;
